import { Document, Node as ContentfulNode, Text, Block, TopLevelBlock } from '@contentful/rich-text-types';
import { ContentfulRichTextBlock, DocumentWithEmbeddedEntries } from 'libs/contentful/types';

/**
 * Replaces dynamic tokens in a plain text string with their corresponding dynamic values.
 * For example, "Get [bonus_amount] when you sign up!" becomes "Get $100 when you sign up!"
 *
 * @param text - The plain text string to process.
 * @param dynamicCopyTokens - Object containing token names as keys and their replacement values as values.
 * @returns The processed text string.
 */
export const replaceTokensInText = (text: string, dynamicCopyTokens: Record<string, string>): string => {
  const tokenNames = Object.keys(dynamicCopyTokens);
  if (tokenNames.length === 0) return text;

  const pattern = new RegExp(`\\[(${tokenNames.join('|')})\\]`, 'g');
  return text.replace(pattern, (_, tokenName: string) => dynamicCopyTokens[tokenName]);
};

/**
 * Replaces dynamic tokens in the document with their corresponding dynamic values.
 * For example, [currentDate] will be replaced with the current date.
 *
 * @param document - The RTE content to process.
 * @param dynamicCopyTokens - Object containing token names as keys and their replacement values as values.
 * @returns The processed RTE content.
 */
export const replaceDynamicTokens = (document: Document, dynamicCopyTokens: Record<string, string>): Document => {
  if (Object.keys(dynamicCopyTokens).length === 0) {
    return document;
  }

  const processNode = (node: ContentfulNode): ContentfulNode => {
    if (node.nodeType === 'text') {
      const textNode = node as Text;
      const processedValue = replaceTokensInText(textNode.value, dynamicCopyTokens);
      if (processedValue === textNode.value) return node;
      return { ...textNode, value: processedValue } as Text;
    }

    if ('content' in node && Array.isArray(node.content)) {
      const blockNode = node as Block;
      const newContent = blockNode.content.map(processNode);
      if (newContent.every((child, i) => child === blockNode.content[i])) return node;
      return { ...blockNode, content: newContent } as Block;
    }

    return node;
  };

  const newContent = document.content.map(processNode) as TopLevelBlock[];
  if (newContent.every((child, i) => child === document.content[i])) return document;
  return { ...document, content: newContent };
};

/**
 * Type guard to check if a value is a rich text field (has a json property with Document)
 */
// Type predicate: the input must be as wide as what callers pass.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
const isRichTextField = (value: unknown): value is ContentfulRichTextBlock | DocumentWithEmbeddedEntries => {
  return (
    // Narrows an unparsed value before reading it; the parse happens here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    typeof value === 'object' &&
    value !== null &&
    'json' in value &&
    // Narrows an unparsed value before reading it; the parse happens here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    typeof value.json === 'object' &&
    value.json !== null &&
    'nodeType' in value.json &&
    value.json.nodeType === 'document'
  );
};

/**
 * Type guard to check if a value is a plain text field (string that might contain tokens)
 */
// Type predicate: the input must be as wide as what callers pass.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
const isPlainTextField = (value: unknown): value is string => {
  // Narrows an unparsed value to a primitive before using it.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  return typeof value === 'string' && value.includes('[') && value.includes(']');
};

/**
 * Recursively processes an object to replace dynamic tokens in both rich text and plain text fields
 *
 * This function searches through the entire object structure and finds:
 * 1. Rich text fields (objects with a `json` property containing a Contentful Document)
 * 2. Plain text fields (strings that contain token patterns like [token_name])
 *
 * For each field found, it applies token replacement using the provided tokens.
 *
 * @example
 * ```typescript
 * // Original data with both rich text and plain text:
 * const data = {
 *   title: "Welcome [user_name]!", // plain text
 *   content: { json: { nodeType: 'document', content: [] } } // rich text
 * };
 *
 * // Tokens: { user_name: "John", bonus_amount: "$100" }
 * // Result: Both fields get token replacement applied
 *
 * const processedData = replaceAllDynamicTokens(data, { user_name: "John", bonus_amount: "$100" });
 * ```
 *
 * @param data - The data object to process
 * @param tokens - The token replacement map (token names as keys, replacement values as values)
 * @returns A new object with the same shape as data but with replaced content
 */
export const replaceAllDynamicTokens = <T>(data: T, tokens: Record<string, string>): T => {
  // If no tokens provided, return original data
  if (!tokens || Object.keys(tokens).length === 0) {
    return data;
  }

  // Tracks containers being / already processed so cyclic graphs (possible once
  // Note's rich-text data.target is resolved) terminate instead of overflowing
  // the stack. While a container is in progress its original is stored as a
  // sentinel, so a back-reference inside a cycle resolves to the original and
  // ends the recursion; once processed, the memo is updated to the result so a
  // shared (non-cyclic) reference reuses the processed value on later paths.
  const seen = new WeakMap<object, unknown>();

  // Recursive walker over arbitrary page JSON: it cannot narrow its input, and it returns it with tokens replaced.
  // oxlint-disable-next-line anti-slop/no-unknown-returns, anti-slop/no-unknown-parameters
  const processValue = (value: unknown): unknown => {
    // Handle null/undefined
    if (value == null) {
      return value;
    }

    // Handle rich text fields
    if (isRichTextField(value)) {
      // An accumulator keyed by a name computed at runtime, so the index signature is the contract.
      // oxlint-disable-next-line anti-slop/no-known-value-widening, anti-slop/no-unsafe-dictionary-type
      const processed: Record<string, unknown> = {
        ...value,
        json: replaceDynamicTokens(value.json, tokens),
      };

      // Also process links (contains embedded entries like Disclosures with their own rich text)
      if ('links' in value && value.links) {
        processed.links = processValue(value.links);
      }

      // Returns into the walker's `unknown` contract, which its recursive input requires.
      // oxlint-disable-next-line anti-slop/no-known-value-widening
      return processed;
    }

    // Handle plain text fields that contain tokens
    if (isPlainTextField(value)) {
      return replaceTokensInText(value, tokens);
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (seen.has(value)) return seen.get(value);
      seen.set(value, value);
      const newArray = value.map(processValue);
      if (newArray.every((item, i) => item === value[i])) return value;
      seen.set(value, newArray);
      return newArray;
    }

    // Handle Maps (preserve Map instances)
    if (value instanceof Map) {
      if (seen.has(value)) return seen.get(value);
      seen.set(value, value);
      let changed = false;
      const processedMap = new Map();
      for (const [key, val] of value.entries()) {
        const processed = processValue(val);
        if (processed !== val) changed = true;
        processedMap.set(key, processed);
      }
      if (!changed) return value;
      seen.set(value, processedMap);
      // Returns into the walker's `unknown` contract, which its recursive input requires.
      // oxlint-disable-next-line anti-slop/no-known-value-widening
      return processedMap;
    }

    // Handle objects (recursively process all properties)
    // Narrows an unparsed value before reading it; the parse happens here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof value === 'object') {
      if (seen.has(value)) return seen.get(value);
      seen.set(value, value);
      let changed = false;
      // Holds an unparsed value; the reader narrows it at the point of use.
      // oxlint-disable-next-line anti-slop/no-unsafe-dictionary-type
      const result: Record<string, unknown> = {};

      // Holds an unparsed value; the reader narrows it at the point of use.
      // oxlint-disable-next-line anti-slop/no-unsafe-dictionary-type
      for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        const processed = processValue(val);
        if (processed !== val) changed = true;
        result[key] = processed;
      }

      if (!changed) return value;
      seen.set(value, result);
      // Returns into the walker's `unknown` contract, which its recursive input requires.
      // oxlint-disable-next-line anti-slop/no-known-value-widening
      return result;
    }

    // Return primitive values as-is
    return value;
  };

  return processValue(data) as T;
};
